// Centro de control (Sentinel, web). La bitácora nueva: intentos de paso en vivo, por punto
// de acceso, con filtros y la línea de tiempo de cada intento. Solo para comunidades con
// Sentinel (`sentinel_mode` distinto de `off`). Ver api/modules/Sentinel/README.md.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { useQueryClient } from '@tanstack/react-query';
import useActiveAdminCommunity from '@/hooks/useActiveAdminCommunity';
import { controlCenterCss } from './styles.web';
import ZoneRail from './ZoneRail.web';
import FilterBar from './FilterBar.web';
import PassageFeed from './PassageFeed.web';
import PassageDetail from './PassageDetail.web';
import {
  Filters, LiveStatus, PERIOD_LABELS, Passage, Period, Summary,
  matchesFilters, useAccessPoints, useLivePassages, useNow, usePassage, usePassages, useSummary,
} from './sentinel';

const FRESH_MS = 3000;

export default function ControlCenterScreen() {
  const { community, communityId } = useActiveAdminCommunity();
  const enabled = !!community?.sentinel_mode && community.sentinel_mode !== 'off';

  if (!enabled) {
    return (
      <div className="cc" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <style>{controlCenterCss}</style>
        <div className="cc-empty">
          <div className="cc-empty-radar"><span /><span /><span /><b /></div>
          <div className="cc-display" style={{ fontSize: 18, color: 'var(--text)' }}>El centro de control aún no está activo aquí</div>
          <div style={{ fontSize: 13, marginTop: 6, maxWidth: 420 }}>
            Se enciende cuando el equipo de DingDong conecta los accesos de esta comunidad a la bitácora nueva.
          </div>
        </div>
      </div>
    );
  }

  return <ControlCenter communityId={communityId} communityName={community.name} />;
}

function ControlCenter({ communityId, communityName }: { communityId: string; communityName: string }) {
  const queryClient = useQueryClient();
  const now = useNow(1000);

  const [filters, setFilters] = useState<Filters>({ period: 'today' });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Lo que llega en vivo: intentos nuevos aún no cargados, versiones más nuevas de los ya
  // cargados, los "recién llegados" (para la animación) y el pulso por punto de acceso.
  const [liveNew, setLiveNew] = useState<Passage[]>([]);
  const [overrides, setOverrides] = useState<Record<string, Passage>>({});
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const [pulses, setPulses] = useState<Record<string, { at: number; denied: boolean }>>({});

  // Búsqueda con debounce (comunidades de hasta 10k viviendas)
  useEffect(() => {
    const id = setTimeout(() => setFilters(f => ({ ...f, q: search.trim() || undefined })), 300);
    return () => clearTimeout(id);
  }, [search]);

  const passagesQuery = usePassages(communityId, filters);
  const summary = useSummary(communityId, filters.period);
  const points = useAccessPoints(communityId);
  const detail = usePassage(communityId, selected);

  // Al cambiar filtros, lo vivo acumulado ya no aplica: la consulta nueva lo trae
  useEffect(() => { setLiveNew([]); setOverrides({}); }, [filters]);

  const summaryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const refreshSummary = useCallback(() => {
    clearTimeout(summaryTimer.current);
    summaryTimer.current = setTimeout(() => queryClient.invalidateQueries({ queryKey: ['sentinel.summary', communityId] }), 2500);
  }, [queryClient, communityId]);

  const live: LiveStatus = useLivePassages(communityId, {
    onPassage: (p, created) => {
      if (p.access_point) {
        setPulses(prev => ({ ...prev, [p.access_point!.uuid]: { at: Date.now(), denied: p.result === 'denied' } }));
      }
      if (created && matchesFilters(p, filters)) {
        setLiveNew(prev => [p, ...prev.filter(x => x.uuid !== p.uuid)]);
        setFresh(prev => new Set(prev).add(p.uuid));
        setTimeout(() => setFresh(prev => { const next = new Set(prev); next.delete(p.uuid); return next; }), FRESH_MS);
      } else {
        setOverrides(prev => ({ ...prev, [p.uuid]: p }));
      }
      if (selected === p.uuid) queryClient.invalidateQueries({ queryKey: ['sentinel.passage', communityId, p.uuid] });
      refreshSummary();
    },
    onReconnect: () => {
      queryClient.invalidateQueries({ queryKey: ['sentinel.passages', communityId] });
      queryClient.invalidateQueries({ queryKey: ['sentinel.summary', communityId] });
    },
  });

  const passages = useMemo(() => {
    const loaded = passagesQuery.data?.pages.flatMap(page => page.data) ?? [];
    const seen = new Set<string>();
    return [...liveNew, ...loaded]
      .filter(p => (seen.has(p.uuid) ? false : (seen.add(p.uuid), true)))
      .map(p => overrides[p.uuid] ?? p)
      .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
  }, [passagesQuery.data, liveNew, overrides]);

  const selectedPassage = useMemo(() => {
    const fromList = passages.find(p => p.uuid === selected);
    return detail.data ? { ...fromList, ...detail.data } as Passage : fromList;
  }, [passages, selected, detail.data]);

  const patch = useCallback((change: Partial<Filters>) => setFilters(f => ({ ...f, ...change })), []);

  // Atajos: "/" busca, Esc cierra, ↑ ↓ recorren la bitácora
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement)?.tagName === 'INPUT';
      if (e.key === '/' && !typing) { e.preventDefault(); searchRef.current?.focus(); return; }
      if (e.key === 'Escape') { typing ? searchRef.current?.blur() : setSelected(null); return; }
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !typing && passages.length) {
        e.preventDefault();
        const i = passages.findIndex(p => p.uuid === selected);
        const next = e.key === 'ArrowDown' ? Math.min(passages.length - 1, i + 1) : Math.max(0, i - 1);
        setSelected(passages[i === -1 ? 0 : next].uuid);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [passages, selected]);

  const pointName = points.data?.find(p => p.uuid === filters.access_point_id)?.name;
  const onlyPeriod = Object.entries(filters).every(([k, v]) => k === 'period' || !v);

  return (
    <div className="cc">
      <style>{controlCenterCss}</style>

      <header className="cc-header">
        <LivePill status={live} />
        <div className="cc-title">
          <h1 className="cc-display">Centro de control</h1>
          <span>{communityName}</span>
        </div>
        <div className="cc-spacer" />
        <div className="cc-segment">
          {(Object.keys(PERIOD_LABELS) as Period[]).map(p => (
            <button key={p} className={filters.period === p ? 'on' : ''} onClick={() => patch({ period: p })}>
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
        <label className="cc-search">
          <Ionicons name="search" size={15} color="rgba(245,239,249,0.5)" />
          <input
            ref={searchRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Tag, placa, invitado, vivienda…"
          />
          {search ? (
            <button onClick={() => setSearch('')} title="Limpiar"><Ionicons name="close" size={14} color="rgba(245,239,249,0.5)" /></button>
          ) : <span className="cc-kbd">/</span>}
        </label>
        <Clock now={now} />
      </header>

      <Kpis summary={summary.data} filters={filters} onPatch={patch} now={now} />

      <div className={`cc-body${selected ? ' with-detail' : ''}`}>
        <ZoneRail
          points={points.data ?? []}
          summary={summary.data}
          selected={filters.access_point_id}
          pulses={pulses}
          now={now}
          onSelect={uuid => patch({ access_point_id: uuid })}
        />

        <PassageFeed
          header={(
            <FilterBar
              filters={filters}
              pointName={pointName}
              count={onlyPeriod ? summary.data?.totals.total ?? null : null}
              onChange={patch}
              onClear={() => { setSearch(''); setFilters(f => ({ period: f.period })); }}
            />
          )}
          passages={passages}
          selected={selected}
          fresh={fresh}
          loading={passagesQuery.isLoading}
          hasMore={!!passagesQuery.hasNextPage}
          loadingMore={passagesQuery.isFetchingNextPage}
          now={now}
          filtered={!onlyPeriod}
          onLoadMore={() => passagesQuery.fetchNextPage()}
          onSelect={uuid => setSelected(uuid === selected ? null : uuid)}
        />

        {selected && (
          <PassageDetail passage={selectedPassage} loading={detail.isLoading} onClose={() => setSelected(null)} />
        )}
      </div>
    </div>
  );
}

function LivePill({ status }: { status: LiveStatus }) {
  const label = status === 'live' ? 'En vivo' : status === 'connecting' ? 'Conectando' : 'Sin conexión';
  return (
    <span className={`cc-live ${status === 'live' ? '' : status}`} title={status === 'offline' ? 'Se recarga sola al reconectar' : undefined}>
      <span className="cc-live-dot" />
      {label}
    </span>
  );
}

function Clock({ now }: { now: number }) {
  const d = new Date(now);
  return (
    <div className="cc-clock cc-num">
      {d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
      <small>{d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })}</small>
    </div>
  );
}

function Kpis({ summary, filters, onPatch, now }: {
  summary?: Summary; filters: Filters; onPatch: (p: Partial<Filters>) => void; now: number;
}) {
  const t = summary?.totals;
  const tiles: { key: string; label: string; icon: string; value?: number; on: boolean; toggle?: Partial<Filters>; className?: string }[] = [
    { key: 'total', label: 'Intentos', icon: 'pulse-outline', value: t?.total, on: false },
    { key: 'entries', label: 'Entradas', icon: 'enter-outline', value: t?.entries, on: filters.direction === 'entry', toggle: { direction: filters.direction === 'entry' ? undefined : 'entry' } },
    { key: 'exits', label: 'Salidas', icon: 'exit-outline', value: t?.exits, on: filters.direction === 'exit', toggle: { direction: filters.direction === 'exit' ? undefined : 'exit' } },
    { key: 'guests', label: 'Visitas', icon: 'qr-code-outline', value: t?.guests, on: filters.method === 'guest_qr', toggle: { method: filters.method === 'guest_qr' ? undefined : 'guest_qr' } },
    { key: 'denied', label: 'Negados', icon: 'close-circle-outline', value: t?.denied, on: filters.result === 'denied', toggle: { result: filters.result === 'denied' ? undefined : 'denied' }, className: 'denied' },
  ];

  return (
    <div className="cc-kpis">
      {tiles.map(tile => (
        <div
          key={tile.key}
          className={`cc-kpi ${tile.className ?? ''}${tile.toggle ? ' clickable' : ''}${tile.on ? ' on' : ''}`}
          onClick={() => tile.toggle && onPatch(tile.toggle)}
        >
          <label><IonIcon name={tile.icon} />{tile.label}</label>
          <strong className="cc-num"><span key={tile.value} className="cc-bump">{tile.value ?? '–'}</span></strong>
        </div>
      ))}
      <Chart summary={summary} now={now} />
    </div>
  );
}

function IonIcon({ name }: { name: string }) {
  return <Ionicons name={name as any} size={13} color="currentColor" />;
}

function Chart({ summary, now }: { summary?: Summary; now: number }) {
  const buckets = summary?.buckets ?? [];
  const max = Math.max(1, ...buckets.map(b => b.granted + b.denied));
  const byDay = summary?.bucket === 'day';
  const label = (iso: string) => byDay
    ? new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })
    : `${new Date(iso).getHours()}:00`;
  const isNow = (iso: string) => {
    const start = new Date(iso).getTime();
    return now >= start && now < start + (byDay ? 86_400_000 : 3_600_000);
  };
  const peak = buckets.reduce((best, b) => (b.granted + b.denied > best.granted + best.denied ? b : best), buckets[0] ?? { start: '', granted: 0, denied: 0 });

  return (
    <div className="cc-chart">
      <div className="cc-chart-head">
        <span>Actividad {byDay ? 'por día' : 'por hora'}</span>
        {peak?.start && peak.granted + peak.denied > 0 && <span>Pico: {label(peak.start)}</span>}
      </div>
      <div className="cc-bars">
        {buckets.map(b => (
          <div key={b.start} className={`cc-bar${isNow(b.start) ? ' now' : ''}`}>
            <i className="g" style={{ height: `${(b.granted / max) * 100}%` }} />
            <i className="d" style={{ height: `${(b.denied / max) * 100}%` }} />
            <span className="cc-bar-tip">{label(b.start)} · {b.granted} pasaron{b.denied ? ` · ${b.denied} negados` : ''}</span>
          </div>
        ))}
      </div>
      {buckets.length > 0 && (
        <div className="cc-axis">
          <span>{label(buckets[0].start)}</span>
          <span>{label(buckets[Math.floor(buckets.length / 2)].start)}</span>
          <span>{label(buckets[buckets.length - 1].start)}</span>
        </div>
      )}
    </div>
  );
}
